
import { FC, useReducer, createContext, useContext, Dispatch, ReactNode } from 'react'
import { getFromSession, storeInSession } from '../Utils'
import { ScoreboardData, FaultHistoryItem } from '../Types'

const sessionFaultId = getFromSession('faultId')

type TroubleshootState = {
    faultId: string | undefined
    faultHistory: FaultHistoryItem[] | undefined
    scoreboard: ScoreboardData | undefined
}

const initialState: TroubleshootState = {
    faultId: !sessionFaultId ? undefined : sessionFaultId,
    faultHistory: undefined,
    scoreboard: undefined
}

const TroubleshootContext = createContext<{
    tsState: TroubleshootState
    tsDispatch: Dispatch<any>
}>({ tsState: initialState, tsDispatch: () => null })

enum TroubleshootActionType {
    SAVE_FAULT_ID,
    SAVE_FAULT_HISTORY,
    SAVE_SCOREBOARD
}

export type TroubleshootAction =
    {
        type: TroubleshootActionType.SAVE_FAULT_ID
        payload: string
    } |
    {
        type: TroubleshootActionType.SAVE_FAULT_HISTORY
        payload: FaultHistoryItem[]
    } |
    {
        type: TroubleshootActionType.SAVE_SCOREBOARD
        payload: ScoreboardData
    }

const troubleshootReducer = (state: TroubleshootState, action: TroubleshootAction): TroubleshootState => {
    switch (action.type) {
        case TroubleshootActionType.SAVE_FAULT_ID:
            if (typeof action.payload === 'string') storeInSession('faultId', action.payload)
            return { ...state, faultId: action.payload } as TroubleshootState
        case TroubleshootActionType.SAVE_FAULT_HISTORY:
            return { ...state, faultHistory: action.payload } as TroubleshootState
        case TroubleshootActionType.SAVE_SCOREBOARD:
            return { ...state, scoreboard: action.payload } as TroubleshootState
        default:
            return state
    }
}

const TroubleshootProvider: FC<{ children: ReactNode }> = ({ children }: { children: ReactNode }) => {
    const [tsState, tsDispatch] = useReducer(troubleshootReducer, initialState)
    return (
        <TroubleshootContext.Provider value={{ tsState, tsDispatch }}>
            {children}
        </TroubleshootContext.Provider>
    )
}

const useTroubleshootContext = () => {
    const context = useContext(TroubleshootContext)
    if (!context) {
        throw new Error(
            'useTroubleshoot must be used within a TroubleshootProvider. Wrap a parent component in <TroubleshootProvider> to fix this error.'
        )
    }
    return context
}

export { useTroubleshootContext, TroubleshootProvider, TroubleshootActionType }